import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import righticon from "../../assets/images/Chevronright.svg";
import ekeyLogo from "../../assets/images/eKey.webp";
import {
  default as authenticationicon,
  default as passwordicon,
} from "../../assets/images/lock_outline.svg";
import yourprofileicon from "../../assets/images/person_outline.svg";
import notificationsicon from "../../assets/images/notificationsb.svg";
import Chevron from "../../components/common/chevron/chevron";
import ToggleSwitch from "../../components/common/toggleSwitch/toggleSwitch";
import Loader from "../../components/common/Loader/Loader";
import useLanguageTranslate from "../../hooks/useLanguageTranslate";
import {
  isPushEnabled,
  isPushSupported,
  onPushStateChange,
  setPushEnabled,
} from "../../hooks/usePushNotifications";
import { selectProfileDataLoader } from "../../redux/features/profileSlice";
import ChangePassword from "./changePassword";
import ProfileDetails from "./profileDetails";
import "./profileSettings.css";

const ProfileSettings = () => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState({
    twoFactorAuth: false,
  });
  const translate = useLanguageTranslate();
  const isLoading = useSelector(selectProfileDataLoader);
  var ekeyLogin = localStorage.getItem("ekeyLogin") ?? false;
  const [pushToggle, setPushToggle] = useState(false);
  const [isUpdatingPush, setIsUpdatingPush] = useState(false);
  const [pushUnavailable, setPushUnavailable] = useState(false);

  // Hydrate the toggle from the device's current push state.
  useEffect(() => {
    let cancelled = false;
    if (!isPushSupported()) {
      // Push is only wired up inside the native app for now.
      setPushUnavailable(true);
      return undefined;
    }
    if (!cancelled) setPushToggle(isPushEnabled());
    const unsubscribe = onPushStateChange((enabled) => {
      if (!cancelled) setPushToggle(enabled);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const handlePushToggle = async () => {
    if (isUpdatingPush) return;
    const next = !pushToggle;
    setIsUpdatingPush(true);
    setPushToggle(next); // optimistic
    try {
      const finalState = await setPushEnabled(next);
      if (finalState !== next) {
        setPushToggle(finalState);
        if (next && !finalState) {
          toast.error(
            translate(
              "Notification permission was blocked. Please enable it in your device settings.",
              "تم حظر إذن الإشعارات. يرجى تفعيله من إعدادات الجهاز."
            )
          );
        }
      }
    } catch (err) {
      console.error("Failed to update push subscription:", err);
      setPushToggle(!next);
      toast.error(
        translate(
          "Could not update notification preference. Please try again.",
          "تعذر تحديث تفضيل الإشعارات. يرجى المحاولة مرة أخرى."
        )
      );
    } finally {
      setIsUpdatingPush(false);
    }
  };


  const updateToggle = (field) => {
    setFormState((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  return (
    <motion.div
      className="profile-setting-container p-5 ipad:p-4 max767:p-5 max767:mt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {isLoading && <Loader />}
      <motion.div
        className="profileSettings flex justify-between"
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
      >
        <div className="breadcrump flex align-middle gap-10">
          <ul className="inline-flex gap-2">
            <li className="inline-flex">
              <Link
                to="/"
                className="text-xs not-italic font-normal leading-4 text-[#6E7C87]"
              >
                {t("commonLabels.home_label")}
              </Link>{" "}
              <span className="ml-2 rtl:ml-0 rtl:mr-2">
                <img
                  src={righticon}
                  alt="right icon"
                  className="rtl:rotate-180"
                />
              </span>
            </li>
            <li className="bread-active text-xs not-italic font-normal leading-4 inline-flex text-black">
              {t("profileSettings.profile_settings")}
            </li>
          </ul>
        </div>
        <p className="hidden last-login text-xs not-italic font-normal leading-4 text-gray-700">
          {t("commonLabels.last_login_label")} 03 November 2024 at 05:20
        </p>
      </motion.div>
      <div className="content">
        <motion.h1
          className="text-2xl not-italic font-semibold leading-8 mx-0 my-[35px]"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease: "easeOut" }}
        >
          {translate("Profile Settings", "إعدادات الملف الشخصي")}
        </motion.h1>
        <motion.div
          className="yourProfileSection p-4 border-b-1 flex flex-col gap-4 self-stretch px-0 py-6 w-[66%] border-b-[color:var(--Light-Gray-Light-Gray-2,#DDE2E4)] border-b border-solid ipad:w-full max767:w-full"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1, ease: "easeOut" }}
        >
          <Chevron
            className=""
            isDefaultOpen={false}
            Header={
              <div className="flex align-middle items-start gap-2 w-full">
                <img src={yourprofileicon} alt="profile icon" />
                <h1 className="text-[#5B6871] text-base not-italic font-semibold leading-6">
                  {t("profileSettings.your_profile")}
                </h1>
              </div>
            }
          >
            <ProfileDetails />
          </Chevron>
        </motion.div>
        {!ekeyLogin && (
          <motion.div
            className="passwordSection p-4 mt-6 mb-6 border-b-1 flex flex-col gap-4 self-stretch px-0 py-6 w-[66%] border-b-[color:var(--Light-Gray-Light-Gray-2,#DDE2E4)] border-b border-solid ipad:w-full max767:w-full"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18, ease: "easeOut" }}
          >
            <Chevron
              className=""
              isDefaultOpen={false}
              Header={
                <div className="flex align-middle flex items-start gap-2 w-full rtl:pr-0">
                  <img src={passwordicon} alt="lock icon" />
                  <h1 className="text-[#5B6871] text-base not-italic font-semibold leading-6">
                    {t("profileSettings.password")}
                  </h1>
                </div>
              }
            >
              <ChangePassword />
            </Chevron>
          </motion.div>
        )}

        <motion.div
          className="notificationsSection p-4 mt-6 mb-6 border-b-1 flex flex-col gap-4 self-stretch px-0 py-6 w-[66%] border-b-[color:var(--Light-Gray-Light-Gray-2,#DDE2E4)] border-b border-solid ipad:w-full max767:w-full"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.26, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between gap-2 w-full pr-8 ipad:pr-4">
            <div className="flex items-center gap-2">
              <img src={notificationsicon} alt="notifications icon" />
              <h1 className="text-[#5B6871] text-base not-italic font-semibold leading-6">
                {translate("Notifications", "الإشعارات")}
              </h1>
            </div>
            <ToggleSwitch
              isOn={pushToggle}
              onChange={handlePushToggle}
              disabled={isUpdatingPush || pushUnavailable}
            />
          </div>
        </motion.div>
        {ekeyLogin && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18, ease: "easeOut" }}
          >
            <div className="pt-10 flex align-middle items-start gap-2 w-full">
              <img src={authenticationicon} alt="authentication icon" />
              <h1 className="text-[#5B6871] text-base not-italic font-semibold leading-6">
                {t("profileSettings.auth_method")}
              </h1>
            </div>
            <div className="pt-[16px] flex align-middle items-start gap-2 w-full">
              <img src={ekeyLogo} alt="authentication icon" class="h-[48px]" />
            </div>
            <div className="pt-[8px] flex align-middle items-start gap-2 w-full">
              <p className="text-[#48535B] text-base not-italic font-normal leading-6">
                {t("profileSettings.auth_desc")}
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default ProfileSettings;
